import React, { useState, useRef, useCallback, useEffect } from 'react'
import { Modal, Button, Form, Table, Badge } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCog } from '@fortawesome/free-solid-svg-icons'
import { useBulkUpdateRoleType, useSearchRoles } from 'api/hooks/use-roles'

const ROLE_TYPE_OPTIONS = [
  { label: 'Data Access', value: 'data_access' },
  { label: 'Functional', value: 'functional' },
  { label: 'Workflow', value: 'workflow' },
  { label: 'Unassign', value: 'unassigned' }
]

const roleTypeLabel = (value) => {
  const option = ROLE_TYPE_OPTIONS.find((o) => o.value === value)
  return option ? option.label : 'Unassigned'
}

const RoleTypeSettings = ({
  show: controlledShow,
  setShow: setControlledShow,
  hideTrigger = false
}) => {
  // Like SodExport, this supports a self-contained mode (own button + state)
  // and a controlled mode driven by a parent Actions dropdown.
  const isControlled = setControlledShow !== undefined
  const [internalShow, setInternalShow] = useState(false)
  const show = isControlled ? controlledShow : internalShow
  const setShow = isControlled ? setControlledShow : setInternalShow
  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedType, setSelectedType] = useState('')
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [isDragging, setIsDragging] = useState(false)
  const lastClickedIndex = useRef(null)
  const dragStartIndex = useRef(null)
  const dragMode = useRef('select')
  const preDragSelection = useRef(null)

  const bulkUpdate = useBulkUpdateRoleType()
  const rolesQuery = useSearchRoles({ search: searchTerm, enabled: !!searchTerm })
  const matchingRoles = rolesQuery.data?.data || []
  const loading = rolesQuery.isLoading

  const handleSearch = () => {
    if (!search.trim()) return
    setSelectedIds(new Set())
    setSearchTerm(search.trim())
  }

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === matchingRoles.length) return new Set()
      return new Set(matchingRoles.map((r) => r.id))
    })
  }, [matchingRoles])

  const handleCheckboxClick = useCallback((index, e) => {
    const id = matchingRoles[index]?.id
    if (!id) return

    if (e.shiftKey && lastClickedIndex.current !== null) {
      const start = Math.min(lastClickedIndex.current, index)
      const end = Math.max(lastClickedIndex.current, index)
      setSelectedIds((prev) => {
        const next = new Set(prev)
        for (let i = start; i <= end; i++) {
          const roleId = matchingRoles[i]?.id
          if (roleId) next.add(roleId)
        }
        return next
      })
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return next
      })
    }
    lastClickedIndex.current = index
  }, [matchingRoles])

  const handleDragStart = useCallback((index) => {
    dragStartIndex.current = index
    const roleId = matchingRoles[index]?.id
    dragMode.current = selectedIds.has(roleId) ? 'deselect' : 'select'
    preDragSelection.current = new Set(selectedIds)
    setIsDragging(true)
  }, [matchingRoles, selectedIds])

  const handleDragEnter = useCallback((currentIndex) => {
    if (!isDragging || dragStartIndex.current === null) return
    const start = Math.min(dragStartIndex.current, currentIndex)
    const end = Math.max(dragStartIndex.current, currentIndex)
    setSelectedIds(() => {
      const next = new Set(preDragSelection.current)
      for (let i = start; i <= end; i++) {
        const id = matchingRoles[i]?.id
        if (!id) continue
        if (dragMode.current === 'select') next.add(id)
        else next.delete(id)
      }
      return next
    })
  }, [isDragging, matchingRoles])

  useEffect(() => {
    if (!isDragging) return
    const handleMouseUp = () => {
      setIsDragging(false)
      dragStartIndex.current = null
    }
    window.addEventListener('mouseup', handleMouseUp)
    return () => window.removeEventListener('mouseup', handleMouseUp)
  }, [isDragging])

  const handleBulkAssign = () => {
    const roleIds = [...selectedIds]
    bulkUpdate.mutate(
      { roleIds, roleType: selectedType },
      {
        onSuccess: (data) => {
          toast.success(data.message || 'Roles updated successfully')
          setSelectedIds(new Set())
          setSelectedType('')
          rolesQuery.refetch()
        },
        onError: () => {
          toast.error('Failed to update roles')
        }
      }
    )
  }

  const handleClose = () => {
    setShow(false)
    setSearch('')
    setSearchTerm('')
    setSelectedType('')
    setSelectedIds(new Set())
  }

  return (
    <>
      {!hideTrigger && (
        <Button
          variant="light-outline"
          className="bg-white border-secondary btn-action-outline"
          onClick={() => setShow(true)}
          title="Assign Role Types"
        >
          <FontAwesomeIcon icon={faCog} />
        </Button>
      )}

      <Modal show={show} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Assign Role Types</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Search Roles by naming convention</Form.Label>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search"
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button variant="primary" onClick={handleSearch} disabled={loading}>
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </div>
          </Form.Group>

          {matchingRoles.length > 0 && (
            <>
              <span className="d-block mb-1">Select roles you want to assign</span>
              <span className="text-muted mb-2 d-block">
                {matchingRoles.length} role(s) found
              </span>

              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <Table responsive hover size="sm" className="border shadow-sm bg-white small mb-0" style={{ userSelect: isDragging ? 'none' : 'auto' }}>
                <thead>
                  <tr>
                    <th style={{ width: '30px' }} className="py-1">
                      <Form.Check
                        type="checkbox"
                        checked={matchingRoles.length > 0 && selectedIds.size === matchingRoles.length}
                        ref={(el) => {
                          if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < matchingRoles.length
                        }}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th className="py-1">Name</th>
                    <th className="py-1">Status</th>
                    <th className="py-1">Current Type</th>
                  </tr>
                </thead>
                <tbody>
                  {matchingRoles.map((role, index) => (
                    <tr key={role.id} className={selectedIds.has(role.id) ? 'table-active' : ''}>
                      <td
                        onMouseDown={(e) => { e.preventDefault(); handleDragStart(index) }}
                        onMouseEnter={() => handleDragEnter(index)}
                        style={{ cursor: 'pointer' }}
                        className="py-1"
                      >
                        <Form.Check
                          type="checkbox"
                          checked={selectedIds.has(role.id)}
                          onChange={(e) => handleCheckboxClick(index, e)}
                        />
                      </td>
                      <td className="py-1">{role.name}</td>
                      <td className="py-1">
                        <span className="text-muted">{role.status}</span>
                      </td>
                      <td className="py-1">
                        <Badge
                          bg={role.role_type ? 'white' : 'light'}
                          text={role.role_type ? 'primary' : 'muted'}
                          className={role.role_type ? 'border border-light' : ''}
                        >
                          {roleTypeLabel(role.role_type)}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              </div>

              <hr />
              <span className="d-block mb-2">What role type would you like to assign to the selected roles?</span>
              <div className="d-flex gap-2 align-items-center">
                <Form.Select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  style={{ width: 'auto' }}
                >
                  <option value="">Select type...</option>
                  {ROLE_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
                <Button
                  variant="primary"
                  onClick={handleBulkAssign}
                  disabled={!selectedType || selectedIds.size === 0 || bulkUpdate.isLoading}
                >
                  {bulkUpdate.isLoading ? 'Applying...' : `Apply${selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}`}
                </Button>
              </div>
            </>
          )}

          {rolesQuery.isError && (
            <Alert variant="danger">
              Failed to search roles
            </Alert>
          )}

          {matchingRoles.length === 0 && rolesQuery.isSuccess && (
            <div className="text-center text-muted py-3">
              No roles found matching &quot;{searchTerm}&quot;
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export { ROLE_TYPE_OPTIONS, roleTypeLabel }
export default RoleTypeSettings
